import type { LoginResponse } from 'backend/types'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** 部署ID一覧のキー（追加前に persist されたストアには存在しない） */
type ManagerDivisionIdsKey = 'groupManagerDivisionIds' | 'departmentManagerDivisionIds' | 'divisionHeadDivisionIds'

/**
 * ユーザー情報型（ストア保存用）
 * トークンとexpires以外のすべてのログインレスポンス情報を保持
 * （expiresはストアで別フィールドとして管理）
 * 部署ID一覧は、追加前に persist されたストアとの互換のため optional
 */
export type User = Omit<LoginResponse, 'token' | 'expires' | ManagerDivisionIdsKey> &
  Partial<Pick<LoginResponse, ManagerDivisionIdsKey>>

interface AuthStore {
  // State
  user: User | null
  token: string | null
  expires: number | null // 有効期限（日数）: 14 or 0
  _hasHydrated?: boolean // localStorageからの復元が完了したかどうか（テストでのモック時にオプショナル）

  // Actions
  setUser: (loginResponse: LoginResponse) => void
  clearUser: () => void
  getToken: () => string | null
  isAuthenticated: () => boolean
  _setHasHydrated?: (hasHydrated: boolean) => void
}

/**
 * 認証ストア
 *
 * localStorageに永続化され、ブラウザリロード後も認証状態を維持します。
 * expires が 0 の場合（セッション認証）は永続化されますが、有効期限チェックで判定します。
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      expires: null,
      _hasHydrated: false,

      // Actions
      setUser: (loginResponse: LoginResponse) => {
        const { token, expires, ...user } = loginResponse

        set({
          user,
          token,
          expires,
        })
      },

      clearUser: () => {
        set({
          user: null,
          token: null,
          expires: null,
        })
      },

      getToken: () => {
        return get().token
      },

      isAuthenticated: () => {
        const state = get()
        return !!state.token && !!state.user
      },

      _setHasHydrated: (hasHydrated: boolean) => {
        set({ _hasHydrated: hasHydrated })
      },
    }),
    {
      name: 'auth-storage', // localStorageのキー名
      // トークン有効期限を考慮して、特定のフィールドのみを永続化
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        expires: state.expires,
      }),
      // localStorageからの復元が完了したときに呼ばれるコールバック
      onRehydrateStorage: () => (state) => {
        // 復元が完了したらフラグを立てる
        state?._setHasHydrated?.(true)
      },
    },
  ),
)
